import { formatDistanceMeters, formatDurationSeconds } from '@motogo/shared';
import { Bookmark, Compass, Crosshair, Globe, History, Menu, Route, Settings } from 'lucide-react';
import { useEffect, useRef, useState, type FC } from 'react';

import RecordButton from './RecordButton';
import VoiceControls from './VoiceControls';

import type { SpeedUnit } from '@/hooks/useSettings';

// Speed/limit travel as km/h everywhere internally (GPS, Overpass, zoom curve); only the
// readout converts. 1 mph = 1.609344 km/h.
const KPH_PER_MPH = 1.609344;

/** What the route-progress readout needs — set while locked onto the loaded route. */
export interface BarProgress {
  fraction: number;
  remainingMeters: number;
  etaSeconds: number | null;
}

interface Props {
  tracking: boolean;
  showNorthBtn: boolean;
  /** True once at least one GPS fix has arrived — the re-center button needs one to act. */
  hasFix: boolean;
  speedKph: number;
  speedLimit: number | null;
  speedUnit: SpeedUnit;
  progress: BarProgress | null;
  onTrack: () => void;
  onResetNorth: () => void;
  onOpenRoute: () => void;
  onOpenRides: () => void;
  onOpenRoutes: () => void;
  onOpenDiscover: () => void;
  onOpenSettings: () => void;
}

/** ETA as an arrival clock time — riders think "when do I get there", not "in how long". */
function formatEta(etaSeconds: number): string {
  return new Date(Date.now() + etaSeconds * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const BottomBar: FC<Props> = ({
  tracking,
  showNorthBtn,
  hasFix,
  speedKph,
  speedLimit,
  speedUnit,
  progress,
  onTrack,
  onResetNorth,
  onOpenRoute,
  onOpenRides,
  onOpenRoutes,
  onOpenDiscover,
  onOpenSettings,
}) => {
  // Compare in km/h (both sides native) so the unit choice can't flip "over limit".
  const overLimit = speedLimit !== null && speedKph > speedLimit;
  const isMph = speedUnit === 'mph';
  const displaySpeed = isMph ? Math.round(speedKph / KPH_PER_MPH) : speedKph;
  const displayLimit =
    speedLimit === null ? null : isMph ? Math.round(speedLimit / KPH_PER_MPH) : speedLimit;
  const unitLabel = isMph ? 'mph' : 'km/h';

  // Overflow menu — secondary actions (rides / settings) live here so the bar keeps
  // only the primary actions (record + voice + route) plus the speed readout. Route
  // planning (which also owns GPX loading now) is primary: it's the planner sheet.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <>
      {/* Map FABs float above the bar — re-center and reset-north only appear when needed.
          The progress row makes the bar taller, so the stack shifts up with it. */}
      {(!tracking || showNorthBtn) && (
        <div className={progress ? 'mg-fab-stack raised' : 'mg-fab-stack'}>
          {showNorthBtn && (
            <button className="mg-fab" onClick={onResetNorth} title="Reset north" aria-label="Reset north">
              <Compass size={26} />
            </button>
          )}
          {!tracking && (
            <button
              className="mg-fab"
              onClick={onTrack}
              disabled={!hasFix}
              title={hasFix ? 'Re-center' : 'Waiting for GPS…'}
              aria-label="Re-center on GPS"
            >
              <Crosshair size={26} />
            </button>
          )}
        </div>
      )}

      <div className="mg-bottombar">
        {/* Route progress row — remaining distance | fill bar | time left (+ arrival),
            above the main controls while locked onto a route. Big enough to read at a
            riding glance (its predecessor was a 3px strip along the bar's top edge). */}
        {progress && (
          <div className="mg-bar-route">
            <span className="mg-bar-route-dist">{formatDistanceMeters(progress.remainingMeters)}</span>
            <div className="mg-bar-route-track">
              <div
                className="mg-bar-route-fill"
                style={{ width: `${Math.min(100, Math.max(0, progress.fraction * 100)).toFixed(1)}%` }}
              />
            </div>
            <span className="mg-bar-route-time">
              {progress.etaSeconds !== null ? (
                <>
                  {formatDurationSeconds(progress.etaSeconds)}
                  <span className="mg-bar-route-arrival"> · {formatEta(progress.etaSeconds)}</span>
                </>
              ) : (
                '–'
              )}
            </span>
          </div>
        )}

        <div className="mg-bar-main">
          {/* Speed cluster — current speed turns red when over the posted limit;
              the limit is rendered as a Belgian speed sign (white disc, red border) */}
          <div className="mg-speed">
            <div className="mg-speed-value">
              <span className={overLimit ? 'mg-speed-number over-limit' : 'mg-speed-number'}>
                {displaySpeed}
              </span>
              <span className="mg-speed-unit">{unitLabel}</span>
            </div>
            <div className={speedLimit === null ? 'mg-limit-sign unknown' : 'mg-limit-sign'}>
              {displayLimit ?? '--'}
            </div>
          </div>

          <div style={{ flex: 1 }} />

          {/* Ride recording — start/stop, local-first capture */}
          <RecordButton />

          {/* Voice (Phase 4) — join/leave a channel by name */}
          <VoiceControls />

          {/* Route planner — A→B / round trip / GPX (opens the /route sheet) */}
          <button className="mg-icon-btn" onClick={onOpenRoute} title="Plan route" aria-label="Plan route">
            <Route size={26} />
          </button>

          {/* Overflow menu — secondary actions (rides / settings) */}
          <div className="mg-overflow-wrap" ref={menuRef}>
            <button
              className="mg-icon-btn"
              onClick={() => setMenuOpen((o) => !o)}
              title="More"
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <Menu size={26} />
            </button>

            {menuOpen && (
              <div className="mg-overflow-menu" role="menu">
                <button
                  className="mg-overflow-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenRides();
                  }}
                >
                  <History size={20} />
                  <span>Rides</span>
                </button>
                <button
                  className="mg-overflow-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenRoutes();
                  }}
                >
                  <Bookmark size={20} />
                  <span>Routes</span>
                </button>
                <button
                  className="mg-overflow-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenDiscover();
                  }}
                >
                  <Globe size={20} />
                  <span>Discover</span>
                </button>
                <button
                  className="mg-overflow-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenSettings();
                  }}
                >
                  <Settings size={20} />
                  <span>Settings</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BottomBar;
